import React, { useState } from "react";
import {
  Card,
  CardBody,
  Button,
  Container,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Modal,
  Spinner,
} from "reactstrap";
import {
  perPageData,
  showToastMessage,
} from "../../../../common/service/CommonService";
import tableStyles from "../../../../assets/scss/common/table.module.scss";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import Reason, {
  ReasonSubmitPayload,
} from "../../../../common/utilities/Reason/Reason";
import CommonTableIndexWise from "../../../../Components/Common/CommonTableIndexWise";
import sf from "../../../../assets/scss/common/searchFilter.module.scss";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import CommonButton from "../../../../Components/Common/CommonButton";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "../../../../common/constants/PermissionConstants";
import { AliquotFormValues } from "../helper/aliquote.types";
import AddEditAliquot from "../components/AddEditAliquot";
import { queryClient } from "api/queryClient";
import { aliquotColumn } from "../helper/aliquoteconstant";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import { useAliquotQuery } from "../api/useAliquotService";
import { normalizeFormValues } from "helpers/constant";

export interface AliquotMasterRecord extends AliquotFormValues {
  aliquotid?: number;
  isActive?: boolean;
}

const AliquotList: React.FC = () => {
  document.title = "Aliquot | ZENTIXS VISTA";

  /* ------------------------- STATE ------------------------- */
  const [searchText, setSearchText] = useState<string>("");
  const [showAddEdit, setShowAddEdit] = useState<boolean>(false);
  const [editRecord, setEditRecord] = useState<AliquotMasterRecord | null>(
    null,
  );
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] =
    useState<AliquotMasterRecord | null>(null);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [updatedRecord, setUpdatedRecord] = useState<AliquotFormValues | null>(
    null,
  );
  const [isReasonModal, setIsReasonModal] = useState<boolean>(false);
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);

  /* ------------------------- API ------------------------- */
  const { SelectAllQuery, InsertMutation, UpdateMutation, DeleteMutation } =
    useAliquotQuery(searchText, currentPageNumber, perPageData);

  //Audit
  const { SelectAllMutation: AuditMutation } = useAuditService();
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);

  /* ------------------------- DATA ------------------------- */
  const {
    data: queryResponse,
    isLoading,
    isFetching,
  } = SelectAllQuery as {
    data?: { data?: (AliquotMasterRecord & { total_count: number })[] };
    isLoading: boolean;
    isFetching: boolean;
  };

  // ✅ Map API response (aliquot_sort → aliquotnumber)
  const data: (AliquotMasterRecord & { total_count: number })[] = (
    queryResponse?.data ?? []
  ).map((item: any) => ({
    ...item,
    aliquotid: item.aliquotid || item.id,
    aliquot_sort: item.aliquot_sort,
  }));

  /* ------------------------- HELPERS ------------------------- */
  const toggleAddEdit = () => {
    if (showAddEdit) setEditRecord(null);
    setShowAddEdit((p) => !p);
  };

  const toggleReasonModal = () => setIsReasonModal((p) => !p);
  const toggleDeleteModal = () => {
    setDeleteModal((p) => !p);
    setShowDeleteReason(false);
  };
  const toggleDeleteReason = () => setShowDeleteReason((p) => !p);

  /* ------------------------- SEARCH ------------------------- */
  const onSearch = (text: string | null) => {
    setSearchText(text ?? "");
    setCurrentPageNumber(1);
  };

  /* ------------------------- AUDIT ------------------------- */
  const onAuditClick = () => {
    const payload = {
      modulename: "Aliquot",
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Aliquot_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  /* ------------------------- ADD ------------------------- */
  const onAddMaster = () => {
    setEditRecord(null);
    toggleAddEdit();
  };

  /* ------------------------- EDIT ------------------------- */
  const onEditMaster = (item: AliquotMasterRecord) => {
    setEditRecord(item);
    toggleAddEdit();
  };

  /* ------------------------- SAVE ------------------------- */
  const onSaveMaster = (record: AliquotFormValues) => {
    if (editRecord?.aliquotid) {
      setUpdatedRecord(record);
      toggleReasonModal();
      return;
    }

    InsertMutation.mutate(normalizeFormValues(record), {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries({ queryKey: ["aliquot-selectAll"] });
        showToastMessage("success", response?.message);
        SelectAllQuery.refetch?.();
        toggleAddEdit();
        setSearchText("")
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  /* ------------------------- UPDATE ------------------------- */
  const btnReasonClick = (reasonPayload: ReasonSubmitPayload) => {
    if (!editRecord?.aliquotid || !updatedRecord) return;

    const payload = normalizeFormValues({
      aliquotid: editRecord.aliquotid,
      aliquot_sort: updatedRecord.aliquot_sort,
      aliquot_number: updatedRecord.aliquot_number,
      updatereason: reasonPayload.reason,
    });

    UpdateMutation.mutate(payload as any, {
      onSuccess: (response: any) => {
        showToastMessage("success", response?.message);
        queryClient.invalidateQueries({ queryKey: ["aliquot-selectAll"] });
        SelectAllQuery.refetch?.();
        toggleReasonModal();
        toggleAddEdit();
        setEditRecord(null);
        setSearchText("")
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  /* ------------------------- DELETE ------------------------- */
  const onDelete = (item: AliquotMasterRecord) => {
    setSelectedRecord(item);
    toggleDeleteModal();
  };

  const confirmDelete = (reasonPayload: ReasonSubmitPayload) => {
    if (!selectedRecord) return;
    if (reasonPayload.id == null) return;
    const payload = {
      aliquotid: reasonPayload.id,
      updatereason: reasonPayload.reason,
    };

    DeleteMutation.mutate(payload as any, {
      onSuccess: (response: any) => {
        showToastMessage("success", response?.message);

        const totalRecords = data[0]?.total_count || 0;
        const lastPage = Math.ceil((totalRecords - 1) / perPageData);

        setCurrentPageNumber(
          Math.max(1, Math.min(currentPageNumber, lastPage)),
        );

        queryClient.invalidateQueries({ queryKey: ["aliquot-selectAll"] });
        SelectAllQuery.refetch?.();

        toggleDeleteModal();
        setSelectedRecord(null);
        setSearchText("")
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  /* ------------------------- RENDER ------------------------- */
  return (
    <div className="page-content">
      <Container fluid className="px-0">
        <Card className={tableStyles.card}>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <CommonSearch onSearch={onSearch} searchText={searchText} />
              <div className="d-flex gap-2">
                <CommonButton
                  module={PERMISSION_MODULES.ALIQUOT}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  size="sm"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
                <CommonButton
                  module={PERMISSION_MODULES.ALIQUOT}
                  action={PERMISSION_ACTIONS.ADD}
                  color="primary"
                  size="sm"
                  onClick={onAddMaster}
                  outline
                >
                  <i className="ri-add-line me-1" /> Add
                </CommonButton>
              </div>
            </div>

            <CommonTableIndexWise
              moduleName={PERMISSION_MODULES.ALIQUOT}
              columns={aliquotColumn}
              data={data}
              showStatus={false}
              actions={{
                onEdit: onEditMaster,
                onDelete,
              }}
              pageSize={perPageData}
              currentPage={currentPageNumber}
              onPageChange={setCurrentPageNumber}
              totalRecords={data[0]?.total_count || 0}
              loading={isLoading || isFetching}
            />
          </CardBody>
        </Card>

        {/* ADD / EDIT MODAL */}
        <AddEditAliquot
          isOpen={showAddEdit}
          existingData={editRecord}
          onSave={onSaveMaster}
          onCancel={toggleAddEdit}
          title={editRecord ? "Edit Aliquot" : "Add Aliquot"}
          isLoading={InsertMutation.isPending}
        />

        {/* REASON MODAL */}
        {isReasonModal && editRecord?.aliquotid != null && (
          <Reason
            isOpen={isReasonModal}
            toggle={toggleReasonModal}
            action="Update"
            id={editRecord.aliquotid}
            click={btnReasonClick}
            isLoading={UpdateMutation.isPending}
          />
        )}

        {/* DELETE MODAL */}
        {deleteModal && (
          <DeleteModal
            isopen={deleteModal}
            toggle={toggleDeleteModal}
            recordId={
              selectedRecord?.aliquotid || (selectedRecord as any)?.id || 0
            }
            recordName={selectedRecord?.aliquot_sort}
            onDeleteClick={confirmDelete}
            showDeleteReason={showDeleteReason}
            toggleDeleteReason={toggleDeleteReason}
            isLoading={DeleteMutation?.isPending}
          />
        )}
      </Container>

      {/* File Preview Modal */}
      {isPreviewOpen && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size={"xl"}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}
    </div>
  );
};

export default AliquotList;
